import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Collapse, IconButton, Link, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { BoardContract, BoardIssue, BoardPerson } from '../../api/ach/board';
import useModal from '../../hooks/useModal';
import LineItemSpendModal from '../LineItems/LineItemSpendModal';
import AskAiButton from './AskAiButton';
import parseContractName from './contractName';
import { campaignLabel, IssueRef, lineItemPath } from './issueRef';
import IssueRow from './IssueRow';
import ruleLabel from './ruleLabels';
import isIORule from './rules';
import { SEVERITY_COLOR, SEVERITY_META, SEVERITY_ORDER, SeverityDot, worstSeverity } from './severity';

// Height of the sticky band header; rows scroll under it.
const HEAD_H = 40;

interface ContractBandProps {
  contract: BoardContract;
  first: boolean;
  // Viewport offset the header sticks at — the app's own sticky sub-nav sits above it.
  stickyTop: number;
  selectedIssueId: number | null;
  onSelectIssue: (item: IssueRef) => void;
  onAckIssue: (issueId: number) => void;
  onAckGroup: (ruleId: string, contractId: number) => void;
  onAskAiIssue: (item: IssueRef) => void;
  onAskAiContract: (contract: BoardContract) => void;
  askAiEnabled: boolean;
  readOnly?: boolean;
  acking?: boolean;
}

// bulkAckRules returns the rule ids that appear on 2+ open issues within this
// contract — those get a single "Ack all <rule> on this contract" action.
export function bulkAckRules(contract: BoardContract): string[] {
  const counts = new Map<string, number>();
  contract.line_items.forEach((li) =>
    li.issues.forEach((issue) => {
      if (issue.state === 'open') {
        counts.set(issue.rule_id, (counts.get(issue.rule_id) ?? 0) + 1);
      }
    })
  );
  return [...counts.entries()].filter(([, n]) => n >= 2).map(([rule]) => rule);
}

// rollupTrafficker inspects the trafficker across EVERY issue in the contract.
// `uniform` is true when they all share one id (null = "unassigned" counts as a value):
// uniform groups omit the per-row trafficker. `person` is the trafficker to show once
// on the header — set only for a uniform, assigned group (null for mixed groups and for
// uniformly-unassigned groups, which have nobody to show).
export function rollupTrafficker(contract: BoardContract): { uniform: boolean; person: BoardPerson | null } {
  let seen: BoardPerson | null = null;
  let count = 0;
  const distinct = new Set<number | null>();
  contract.line_items.forEach((li) =>
    li.issues.forEach((issue) => {
      distinct.add(issue.trafficker?.id ?? null);
      count += 1;
      if (issue.trafficker) {
        seen = issue.trafficker;
      }
    })
  );
  const uniform = count > 0 && distinct.size === 1;
  return { uniform, person: uniform ? seen : null };
}

// rollupCampaign returns the representative campaign shared by EVERY io-grain issue on a
// line item, so it can be shown once on the IO sub-header. null when they disagree (or
// any of them is unattributed) — those rows keep their own per-row "via".
export function rollupCampaign(issues: BoardIssue[]): string | null {
  const labels = issues.filter((issue) => isIORule(issue.rule_id)).map(campaignLabel);
  if (labels.length === 0 || labels.some((label) => label === '')) {
    return null;
  }
  return labels.every((label) => label === labels[0]) ? labels[0] : null;
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

// IoSubHeader is the rail above an insertion order's rows: the line-item link, its
// id (labelled "IO #…" so it is never read as a campaign id) and issue count on the
// left; the IO-scoped meta right-aligned — the representative campaign when every
// io-grain row agrees on one, and the single daily-spend action (keyed per insertion
// order, so the rows no longer each carry a copy).
function IoSubHeader({
  contractId,
  lineItemId,
  ioName,
  count,
  campaign,
  showSpend,
}: {
  contractId: number;
  lineItemId: number;
  ioName: string;
  count: number;
  campaign: string | null;
  showSpend: boolean;
}) {
  const [showSpendModal, hideSpendModal] = useModal(
    () => <LineItemSpendModal lineItemId={lineItemId} contractId={contractId} onClose={hideSpendModal} />,
    { clickOutsideCloses: true },
    [lineItemId, contractId]
  );

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        px: 1.5,
        pl: 3.5,
        py: 0.25,
        minHeight: 28,
        bgcolor: 'action.hover',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Link
        component={RouterLink}
        to={lineItemPath(contractId, lineItemId)}
        target="_blank"
        rel="noopener noreferrer"
        variant="caption"
        fontWeight={600}
        noWrap
        data-testid={`io-link-${lineItemId}`}
        sx={{ minWidth: 0, fontSize: 13 }}
      >
        {ioName}
      </Link>
      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: 12 }} noWrap>
        {`IO #${lineItemId}`}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 12 }} noWrap>
        {`· ${count}`}
      </Typography>

      <Box sx={{ flex: '1 1 auto' }} />

      {(campaign || showSpend) && (
        // Its own cluster with a full gap either side: the rail's meta must never crowd
        // the id block on its left or read as one run-on string.
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: '0 1 auto', minWidth: 0, ml: 2 }}>
          {campaign && (
            <Typography
              variant="caption"
              color="text.disabled"
              noWrap
              data-testid={`io-campaign-attr-${lineItemId}`}
              sx={{ flex: '0 1 auto', minWidth: 0, fontSize: 12 }}
            >
              {`via ${campaign}`}
            </Typography>
          )}
          {showSpend && (
            <Button
              variant="text"
              size="small"
              onClick={showSpendModal}
              data-testid={`io-spend-${lineItemId}`}
              sx={{
                flex: 'none',
                p: 0,
                minWidth: 0,
                fontSize: 12,
                lineHeight: 1.6,
                textTransform: 'none',
                fontWeight: 400,
                whiteSpace: 'nowrap',
              }}
            >
              Daily spend
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

// ContractBand is one contract in the ledger: a sticky header (advertiser, the rest of
// the pipe-delimited name muted, the owner rollup,
// per-severity dot counts, contract-scope AI triage, collapse caret), an optional
// bulk-ack bar, then slim IO sub-headers over the issue rows. The left edge is tinted
// with the contract's worst severity.
function ContractBand({
  contract,
  first,
  stickyTop,
  selectedIssueId,
  onSelectIssue,
  onAckIssue,
  onAckGroup,
  onAskAiIssue,
  onAskAiContract,
  askAiEnabled,
  readOnly = false,
  acking = false,
}: ContractBandProps) {
  const [open, setOpen] = React.useState(true);
  const { advertiser, rest } = parseContractName(contract.contract_name);
  const bulkRules = readOnly ? [] : bulkAckRules(contract);
  const { uniform, person } = rollupTrafficker(contract);
  const worst = worstSeverity(contract.severity_counts);
  const toggle = () => setOpen((v) => !v);

  return (
    <Box
      data-testid={`contract-card-${contract.contract_id}`}
      sx={{ borderTop: first ? 0 : '1px solid', borderColor: 'divider' }}
    >
      {/* The whole header row is the collapse target; the caret stays the focusable
          control that carries aria-expanded, so the row itself is presentational. */}
      <Stack
        role="presentation"
        onClick={toggle}
        data-testid={`contract-header-${contract.contract_id}`}
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          position: 'sticky',
          top: stickyTop,
          zIndex: 1,
          minHeight: HEAD_H,
          px: 1.5,
          py: 0.5,
          minWidth: 0,
          overflow: 'hidden',
          cursor: 'pointer',
          // Three surface levels, darkest first: band header > IO rail (action.hover,
          // ~grey[100]) > white issue rows. Opaque, not an alpha tint: rows scroll under it.
          bgcolor: (t) => t.palette.grey[200],
          borderLeft: '3px solid',
          borderLeftColor: worst ? SEVERITY_COLOR[worst] : 'divider',
        }}
      >
        <IconButton
          size="small"
          aria-label={open ? 'Collapse contract' : 'Expand contract'}
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          data-testid={`contract-caret-${contract.contract_id}`}
          sx={{ p: 0.25, transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>

        {/* subtitle2 carries the theme's green link colour; the contract name is not a
            link, so it takes high-contrast ink over the band surface instead. */}
        <Typography
          variant="subtitle2"
          fontWeight={650}
          noWrap
          sx={{ flex: 'none', color: 'text.primary' }}
        >
          {advertiser || `Contract ${contract.contract_id}`}
        </Typography>

        {rest && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 40 }} title={rest}>
            {rest}
          </Typography>
        )}

        {person && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ flex: 'none' }}
            data-testid={`contract-trafficker-${contract.contract_id}`}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9.5,
                fontWeight: 700,
                bgcolor: 'action.selected',
                color: 'text.secondary',
              }}
            >
              {initials(person.name)}
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap>
              {person.name}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" spacing={1.25} sx={{ ml: 'auto', flex: 'none' }}>
          {SEVERITY_ORDER.filter((sev) => contract.severity_counts[sev] > 0).map((sev) => (
            <Stack
              key={sev}
              direction="row"
              spacing={0.5}
              alignItems="center"
              data-testid={`contract-sev-${contract.contract_id}-${sev}`}
            >
              <SeverityDot severity={sev} />
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                title={`${contract.severity_counts[sev]} ${SEVERITY_META[sev].label}`}
              >
                {contract.severity_counts[sev]}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {askAiEnabled && (
          <AskAiButton
            label="Triage with AI"
            onClick={() => onAskAiContract(contract)}
            testId={`contract-ask-ai-${contract.contract_id}`}
          />
        )}
      </Stack>

      <Collapse in={open} unmountOnExit>
        {bulkRules.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ px: 1.5, pl: 3.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
            data-testid="bulk-ack-toolbar"
          >
            {bulkRules.map((rule) => (
              <Chip
                key={rule}
                size="small"
                color="primary"
                variant="outlined"
                disabled={acking}
                onClick={() => onAckGroup(rule, contract.contract_id)}
                label={`Ack all “${ruleLabel(rule)}” on this contract`}
              />
            ))}
          </Stack>
        )}

        {contract.line_items.map((li) => {
          const ioName = li.io_name || `IO ${li.insertion_order_id}`;
          const rollupCampaignName = rollupCampaign(li.issues);
          return (
            <Box key={li.insertion_order_id}>
              <IoSubHeader
                contractId={contract.contract_id}
                lineItemId={li.insertion_order_id}
                ioName={ioName}
                count={li.issues.length}
                campaign={rollupCampaignName}
                showSpend={li.issues.some((issue) => isIORule(issue.rule_id))}
              />
              {li.issues.map((issue) => {
                const item: IssueRef = {
                  contract,
                  insertionOrderId: li.insertion_order_id,
                  ioName,
                  issue,
                };
                return (
                  <IssueRow
                    key={issue.id}
                    item={item}
                    selected={selectedIssueId === issue.id}
                    onSelect={onSelectIssue}
                    onAck={onAckIssue}
                    onAskAi={onAskAiIssue}
                    askAiEnabled={askAiEnabled}
                    acking={acking}
                    readOnly={readOnly}
                    showTrafficker={!uniform}
                    showCampaignAttr={rollupCampaignName === null}
                    scrollMarginTop={stickyTop + HEAD_H}
                  />
                );
              })}
            </Box>
          );
        })}
      </Collapse>
    </Box>
  );
}

export default ContractBand;
